<template>
  <div>
    <v-dialog
      v-model="showEditPopup"
      persistent
      max-width="900"
    >
      <v-card :class="$style.card">
        <v-card-title class="headline pt-0">
          {{ dataType === 'default' ?
          $t('profileSettings.billingExport.header') :
          $t('profileSettings.billingExportSecondary.header')
          }}
        </v-card-title>
        <v-card-text class="pt-0">

          <v-row class="align-center">
            <v-col>
              <v-text-field
                  v-model="exportSettings.name"
                  v-validate="{ required: true }"
                  name="name"
                  maxlength="100"
                  :error-messages="errors.collect('name')"
                  :label="$t('profileSettings.billingExport.name')"
              />
            </v-col>

            <v-col
              class="d-flex justify-end"
              cols="auto"
            >
              <v-checkbox
                  v-model="exportSettings.isActive"
                  :class="$style.checkboxField"
                  :label="$t('profileSettings.billingExport.active')"
                  name="isActive"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <div :class="$style.expansionPanelsContainer">
          <v-expansion-panels
              v-model="activePanel"
              focusable
              flat
          >
            <v-expansion-panel>
              <v-expansion-panel-header>
                Notification Settings
              </v-expansion-panel-header>
              <v-expansion-panel-content
                eager
                class="pt-5"
              >
                <v-form
                    :class="$style.emailInputContainer"
                    @submit.prevent="addEmail"
                >
                  <v-text-field
                      v-model="newEmail"
                      v-validate="{ email: true }"
                      color="blue darken-2"
                      type="email"
                      name="newEmail"
                      autocomplete="email"
                      :label="$t('profileSettings.newEmailBtn')"
                      :error-messages="errors.collect('newEmail')"
                  ></v-text-field>
                  <v-btn
                      color="error"
                      type="submit"
                      :class="$style.emailAddButton"
                  >
                    {{ $t('buttons.add') }}
                  </v-btn>
                </v-form>
                <div :class="$style.emailsContainer">
                  <span
                      v-if="showEmailsValidationError"
                      :class="$style.error"
                  >
                    At least one email is required.
                  </span>
                  <v-chip
                      v-for="email in exportSettings.notificationEmails"
                      :key="email"
                      :class="$style.emailChip"
                      close
                      @click:close="showDeleteEmailPopup(email)"
                  >
                    {{ email }}
                  </v-chip>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('profileSettings.billingExport.ftpSettingsTitle') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content
                eager
                class="pt-5"
              >
                <div
                    :id="formId"
                    :class="$style.column"
                >
                  <v-select
                      key="exportTypeId"
                      v-model="exportSettings.exportTypeId"
                      v-validate="{ required: true }"
                      :items="billingExportTypes"
                      :label="$t('profileSettings.billingExport.type')"
                      item-value="exportTypeId"
                      item-text="name"
                      single-line
                      name="exportType"
                      :error-messages="errors.collect('exportType')"
                      @change="changeExportType"
                  />
                  <div v-if="!isMedicClipboardSFTP">
                    <v-text-field
                        key="server"
                        v-model="exportSettings.server"
                        v-validate="{ required: true, url: true }"
                        name="server"
                        maxlength="255"
                        :label="isNemsisSoap
                          ? $t('profileSettings.billingExport.endpoint')
                          : $t('profileSettings.billingExport.server')"
                        :error-messages="errors.collect('server')"
                    />
                    <v-text-field
                        v-if="isNemsisSoap"
                        key="wsdl"
                        v-model="exportSettings.wsdl"
                        v-validate="{ required: true, url: true }"
                        name="wsdl"
                        maxlength="255"
                        :label="$t('profileSettings.billingExport.wsdl')"
                        :error-messages="errors.collect('wsdl')"
                    />
                    <v-text-field
                        v-if="isNemsisSoap"
                        key="organization"
                        v-model="exportSettings.organization"
                        name="organization"
                        maxlength="50"
                        :label="$t('profileSettings.billingExport.organization')"
                        :error-messages="errors.collect('organization')"
                    />
                    <v-text-field
                        key="username"
                        v-model="exportSettings.username"
                        v-validate="{ required: true }"
                        name="username"
                        maxlength="100"
                        :error-messages="errors.collect('username')"
                        :label="$t('profileSettings.billingExport.user')"
                    />
                    <v-text-field
                        v-if="isPasswordEditMode || isFirstSetupMode"
                        key="newPassword"
                        v-model="newPassword"
                        v-validate="{ required: isFirstSetupMode }"
                        :label="$t('profileSettings.billingExport.currentPassword')"
                        type="password"
                        append-icon="lock"
                        persistent-hint
                        maxlength="100"
                        :error-messages="errors.collect('newPassword')"
                        name="newPassword"
                        autocomplete="new-password"
                        @change="isPasswordChanged = true"
                    />
                    <v-text-field
                        v-if="!isPasswordEditMode && !isFirstSetupMode"
                        value="******"
                        :label="$t('profileSettings.billingExport.currentPassword')"
                        readonly
                        append-icon="edit"
                        @click:append="changePassword"
                    />
                    <v-text-field
                        v-if="!isNemsisSoap"
                        key="uploadDirectory"
                        v-model="exportSettings.uploadDirectory"
                        v-validate="{ required: true }"
                        name="uploadDirectory"
                        maxlength="255"
                        :error-messages="errors.collect('uploadDirectory')"
                        :label="$t('profileSettings.billingExport.uploadDirectory')"
                    />
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('profileSettings.billingExport.exportSettingsTitle') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content
                  eager
                  class="pt-5"
              >
                <v-flex :class="$style.row">
                  <div :class="$style.column">
                    <v-checkbox
                        v-model="exportSettings.includeUpdatedIncidents"
                        :class="$style.checkboxField"
                        :label="$t('profileSettings.billingExport.updatedIncidents')"
                        name="includeUpdatedIncidents"
                    />
                    <v-checkbox
                        v-model="exportSettings.noTransport"
                        :class="$style.checkboxField"
                        :label="$t('profileSettings.billingExport.noTransport')"
                        name="noTransport"
                    />
                    <v-checkbox
                        v-model="exportSettings.withoutAddress"
                        :class="$style.checkboxField"
                        :label="$t('profileSettings.billingExport.withoutAddress')"
                        name="withoutAddress"
                    />
                    <v-checkbox
                      v-model="exportSettings.emptyPatientName"
                      :class="$style.checkboxField"
                      :label="$t('profileSettings.billingExport.emptyPatientName')"
                      name="emptyPatientName"
                    />
                    <v-checkbox
                        v-model="exportSettings.unknownPatient"
                        :class="$style.checkboxField"
                        :label="$t('profileSettings.billingExport.unknownPatient')"
                        :disabled="exportSettings.unknownPatientDisabled"
                        name="unknownPatient"
                    />
                    <v-checkbox
                      v-if="$store.state.user.clientId === clientIdentifiers.REDONDO_BEACH"
                      v-model="exportSettings.onlyRedondoBeachZipcodes"
                      :class="$style.checkboxField"
                      :label="$t('profileSettings.billingExport.onlyRedondoBeachZipCodes')"
                      :disabled="true"
                      name="onlyRedondoBeachZipcodes"
                    />
                    <v-checkbox
                      v-if="$store.state.user.clientId === clientIdentifiers.REDONDO_BEACH"
                      v-model="exportSettings.onlyVitalSigns"
                      :class="$style.checkboxField"
                      :label="$t('profileSettings.billingExport.onlyVitalSigns')"
                      :disabled="true"
                      name="onlyVitalSigns"
                    />
                    <v-checkbox
                      v-if="$store.state.user.clientId === clientIdentifiers.REDONDO_BEACH"
                      v-model="exportSettings.withoutDOA"
                      :class="$style.checkboxField"
                      :label="$t('profileSettings.billingExport.withoutDOA')"
                      :disabled="true"
                      name="withoutDOA"
                    />
                  </div>
                  <v-radio-group
                      v-if="!isNemsisSoap"
                      key="format"
                      v-model="exportSettings.format"
                      v-validate="{ required: true }"
                      :label="$t('profileSettings.billingExport.format')"
                      :class="$style.exportFormat"
                      name="format"
                      :error-messages="errors.collect('format')"
                  >
                    <v-radio
                        v-for="format in formats"
                        :key="format.value"
                        :label="`PDF's with ${format.name} file`"
                        :value="format.value"
                    ></v-radio>
                  </v-radio-group>
                </v-flex>
                <v-autocomplete
                    v-model="exportSettings.excludedProviders"
                    :items="providersItems"
                    :label="$t('profileSettings.billingExport.excludeProviders')"
                    multiple
                    chips
                    deletable-chips
                />
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header>
                Schedule Settings
              </v-expansion-panel-header>
              <v-expansion-panel-content
                eager
                class="pt-5"
              >
                <v-text-field
                    v-validate="{ required: !exportSettings.cron }"
                    :value="exportSettings.cron"
                    :label="$t('profileSettings.timeForExport')"
                    readonly
                    name="cron"
                    :error-messages="errors.collect('cron')"
                    append-icon="edit"
                    @click:append="isCronEditMode = !isCronEditMode"
                />
                <VueCronEditorBuefy
                    v-if="isCronEditMode || isFirstSetupMode"
                    v-model="exportSettings.cron"
                    :visibleTabs="['minutes', 'hourly', 'daily', 'weekly', 'monthly', 'advanced']"
                    :preserveStateOnSwitchToAdvanced="true"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header>
                Inclusion/Exclusion Filters
              </v-expansion-panel-header>
              <v-expansion-panel-content
                eager
                class="pt-5"
              >

                <template v-for="(group, index) in groups">
                  <v-card
                      :key="'group_' + index"
                      :class="['mb-2', $style['group-container']]"
                      outlined
                      :rounded="false"
                      tile
                  >
                    <v-toolbar
                        flat
                        dense
                        :class="[$style['group-toolbar']]"
                    >
                      <v-spacer></v-spacer>
                      <v-btn-toggle
                          v-model="group.operator"
                          mandatory
                          :class="[$style['operator-select']]"
                          @change="updateGroupHint(index)"
                      >
                        <v-btn
                            value="listed"
                            small
                            style="min-width: 100px"
                        >
                          Units Listed
                        </v-btn>
                        <v-btn
                            value="not_listed"
                            small
                            style="min-width: 100px"
                        >
                          Units Not Listed
                        </v-btn>
                      </v-btn-toggle>
                      <v-spacer></v-spacer>
                      <v-btn
                          icon
                          small
                          dark
                          @click="removeGroup(index)"
                      >
                        <v-icon small>close</v-icon>
                      </v-btn>
                    </v-toolbar>
                    <v-container>
                      <v-row
                          class="align-center"
                          no-gutters
                      >
                        <v-combobox
                            v-model="group.units"
                            v-validate="{ required: true }"
                            :rules="[required]"
                            :error-messages="errors.collect('units')"
                            label="Units"
                            :items="units"
                            multiple
                            small-chips
                            deletable-chips
                            @change="updateGroupHint(index)"
                        />
                      </v-row>
                      <v-row v-if="group.operator === 'listed' && group.units.length > 0">
                        <v-col
                            cols="9"
                            class="pr-2 align-self-start"
                        >
                          <v-select
                              v-model="group.time"
                              label="Time"
                              :items="times"
                              clearable
                              @change="updateGroupHint(index)"
                          />
                        </v-col>
                        <v-col
                            v-if="group.operator === 'listed' && group.time"
                            cols="auto"
                            class="pr-2 align-self-start"
                        >
                          <v-checkbox
                              v-model="group.timeIsEmpty"
                              label="Empty Time"
                              @change="updateGroupHint(index)"
                          />
                        </v-col>
                      </v-row>
                      <i
                          v-if="group.units.length > 0"
                          :class="{
                            [$style.redLabel]: groupHints[index] && groupHints[index]?.conflict
                          }"
                      >
                        <b>
                          {{groupHints[index] && groupHints[index]?.conflict
                            ? 'Warning:'
                            : 'Hint:'}}
                        </b>
                        {{ groupHints[index] ? groupHints[index]?.text : '' }}
                      </i>
                    </v-container>
                  </v-card>
                  <div
                      v-if="index < groups.length-1"
                      :key="'group_or_' + index"
                      class="text-center mb-2"
                  >OR</div>
                </template>

                <div class="text-center">
                  <v-btn
                      text
                      @click="addNewGroup"
                  >+ Add Condition</v-btn>
                </div>

              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <v-card-actions class="pb-0">
          <v-spacer/>
          <v-btn
            color="blue darken-1"
            text
            @click.native="showEditPopup = false"
          >{{ $t('buttons.cancel') }}</v-btn>
          <v-btn
            color="primary"
            text
            @click="validateAndSave"
          >{{ $t('buttons.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <confirmation ref="confirmationPopup"/>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import _ from 'lodash';
import { UPDATE_BILLING_EXPORT_SETTINGS } from '@/store/ProfileSettings/actions';
import lookupApi from '@/api/lookup';
import profileSettingsApi from '@/api/profileSettings';
import CLIENT_IDENTIFIERS from '@/enums/clientIdentifiers';
import editExportPopupMixin from './editExportPopupMixin';

export default {
  name: 'EditBillingExportPopup',
  mixins: [editExportPopupMixin],
  data() {
    return {
      activePanel: null,
      units: [],
      times: [
        'Dispatch',
        'Arrival',
        'AtPatient',
        'LeftScene',
        'AtFacility',
        'OnFacilityEquipment',
        'Available',
      ],
      groups: [],
      groupHints: {},
      isLoadingFilters: false,
      isLoadingUnits: false,

      /**
       * Form id for submit by button.
       */
      formId: 'billingExportForm',
      /**
       * Value of new email.
       */
      newEmail: '',
      /**
       * Data type: Default and Secondary
       */
      dataType: 'default',
      /**
       * Flag for show emails list validation error.
       */
      showEmailsValidationError: false,
      /**
       * Formats types list.
       */
      formats: [
        { value: 'xml', name: 'XML' },
        { value: 'tab', name: 'TAB' },
        { value: 'tripToken', name: 'Trip Token' },
      ],

      providers: [],
      /**
       * Export settings model.
       */
      exportSettings: {
        notificationEmails: [],
        exportTypeId: null,
        server: '',
        wsdl: '',
        organization: '',
        username: '',
        uploadDirectory: '',
        includeUpdatedIncidents: false,
        noTransport: false,
        withoutAddress: false,
        unknownPatient: false,
        unknownPatientDisabled: false,
        emptyPatientName: false,
        excludedProviders: null,
        format: 'tab',
        cron: '',
        dataType: 'default',
        exportId: null,
        name: '',
        isActive: true,
        onlyRedondoBeachZipcodes: false,
        onlyVitalSigns: false,
        withoutDOA: false,
      },
      clientIdentifiers: CLIENT_IDENTIFIERS,
    };
  },
  computed: {
    ...mapState('profileSettings', {
      billingExportSettings: state => state.billingExportSettings,
      billingExportSecondarySettings: state => state.billingExportSecondarySettings,
      billingExportTypes: state => state.billingExportUploadTypes,
    }),
    /**
     * Check export type id is the id of medic clipboard sftp type.
     */
    isMedicClipboardSFTP() {
      const MEDIC_CLIPBOARD_SFTP_TYPE_ID = 5;

      return this.exportSettings.exportTypeId === MEDIC_CLIPBOARD_SFTP_TYPE_ID;
    },
    /**
     * Check export type id is the id of nemsis soap type.
     */
    isNemsisSoap() {
      const NEMSIS_SOAP_TYPE_ID = 6;

      return this.exportSettings.exportTypeId === NEMSIS_SOAP_TYPE_ID;
    },
    /**
     * Providers items.
     */
    providersItems() {
      return this.providers.map(item => ({
        text: `${item.code} - ${item.name}`,
        value: item.code,
      }));
    },
  },

  watch: {
    /**
     * Watch for changes in the groups array to update hints
     */
    groups: {
      deep: true,
      handler(newGroups) {
        // Reset hints when groups change
        this.groupHints = {};

        // Initialize hint objects for each group
        newGroups.forEach((group, index) => {
          this.$set(this.groupHints, index, { text: '', conflict: false });
          this.updateGroupHint(index);
        });

        // Check for conflicts after all hints are set
        this.handleConflictionFilter();
      },
    },
  },
  mounted() {
    this.loadProviders();
  },
  methods: {
    ...mapActions('profileSettings', {
      // Using in editExportPopupMixin
      saveSettings: UPDATE_BILLING_EXPORT_SETTINGS,
    }),
    changeExportType() {
      if (!this.isNemsisSoap && this.exportSettings.format === 'nemsis') {
        this.exportSettings.format = 'tab';
      }
    },
    getDataToSave() {
      const settings = _.cloneDeep(this.exportSettings);
      if (this.isNemsisSoap) {
        settings.format = 'nemsis';
        settings.uploadDirectory = null;
      } else {
        settings.wsdl = null;
        settings.organization = null;
      }
      return settings;
    },
    required(value) {
      if (value instanceof Array && value.length === 0) {
        return 'The field is required.';
      }
      return !!value || 'The field is required.';
    },

    /**
     * Load list of providers.
     */
    async loadProviders() {
      const response = await lookupApi.fetchProviders();
      this.providers = response.results;
    },

    /**
     * Load list of units.
     */
    async loadUnits() {
      try {
        this.isLoadingUnits = true;
        const response = await profileSettingsApi.getDeviceUnits();
        this.units = response.units || [];
      } catch (error) {
        console.error('Error loading units:', error);
        this.units = [];
      } finally {
        this.isLoadingUnits = false;
      }
    },

    /**
     * Open popup for edit.
     */
    async showPopup(type) {
      this.initPopupData();
      await this.loadUnits();
      this.activePanel = null;

      let setting = null;
      this.dataType = type;

      if (type === 'default' && this.billingExportSettings) {
        setting = this.billingExportSettings;
      } else if (type === 'secondary' && this.billingExportSecondarySettings) {
        setting = this.billingExportSecondarySettings;
      }

      if (setting != null) {
        this.isFirstSetupMode = false;
        this.exportSettings = {
          notificationEmails: setting.notificationEmails,
          exportTypeId: setting.exportTypeId,
          server: setting.server,
          wsdl: setting.wsdl,
          organization: setting.organization,
          username: setting.username,
          uploadDirectory: setting.uploadDirectory,
          includeUpdatedIncidents: setting.includeUpdatedIncidents,
          noTransport: setting.noTransport,
          withoutAddress: setting.withoutAddress,
          unknownPatient: setting.unknownPatient,
          unknownPatientDisabled: false,
          emptyPatientName: setting.emptyPatientName,
          excludedProviders: setting.excludedProviders,
          format: setting.format,
          cron: setting.cron,
          dataType: type,
          exportId: setting.exportId,
          name: setting.name,
          isActive: setting.isActive,
          onlyRedondoBeachZipcodes: false,
          onlyVitalSigns: false,
          withoutDOA: false,
        };

        // Load filters if export ID is available
        if (setting.exportId) {
          await this.loadFilters(setting.exportId);
        }
      } else {
        this.isFirstSetupMode = true;
        this.exportSettings = {
          notificationEmails: [],
          exportTypeId: null,
          server: '',
          wsdl: '',
          organization: '',
          username: '',
          uploadDirectory: '',
          includeUpdatedIncidents: false,
          noTransport: false,
          withoutAddress: false,
          unknownPatient: false,
          unknownPatientDisabled: false,
          emptyPatientName: false,
          excludedProviders: null,
          format: 'tab',
          cron: '',
          dataType: type,
          exportId: null,
          name: '',
          isActive: true,
          onlyRedondoBeachZipcodes: false,
          onlyVitalSigns: false,
          withoutDOA: false,
        };

        // Initialize empty filters for new export
        this.groups = [];
      }

      if (this.$store.state.user.clientId === this.clientIdentifiers.REDONDO_BEACH) {
        this.exportSettings.unknownPatient = false;
        this.exportSettings.unknownPatientDisabled = true;

        this.exportSettings.onlyRedondoBeachZipcodes = true;
        this.exportSettings.onlyVitalSigns = true;
        this.exportSettings.withoutDOA = true;
      }
    },

    /**
     * Load filters for the export.
     * @param {string} exportId - Export ID
     */
    async loadFilters(exportId) {
      try {
        this.isLoadingFilters = true;
        const response = await profileSettingsApi.getBillingExportFilters(exportId);
        this.groups = response.filters || [];

        // If no filters are returned, initialize with an empty array
        if (!this.groups.length) {
          this.groups = [];
        }
      } catch (error) {
        console.error('Error loading filters:', error);
        this.groups = [];
      } finally {
        this.isLoadingFilters = false;
      }
    },
    /**
     * Add email to notification emails list.
     */
    async addEmail() {
      const isNewEmailValid = await this.$validator.validate('newEmail');
      if (this.newEmail === '' || !isNewEmailValid) {
        return;
      }

      this.showEmailsValidationError = false;
      this.exportSettings.notificationEmails.push(this.newEmail);
      this.newEmail = '';
    },
    /**
     * Remove email from notification emails list.
     */
    deleteEmail(emailToDelete) {
      this.exportSettings.notificationEmails = this.exportSettings.notificationEmails
        .filter(email => email !== emailToDelete);
    },
    /**
     * Show confirmation for deleting email.
     * @param email
     */
    showDeleteEmailPopup(email) {
      this.$refs.confirmationPopup.showConfirm(
        this.$t('profileSettings.deleteEmailTitle'),
        `Do you want to delete ${email} from list?`,
        () => {
          this.deleteEmail(email);
        },
      );
    },
    /**
     * Add a new filter group.
     */
    addNewGroup() {
      const newGroup = {
        operator: 'listed',
        units: [],
        time: null,
        timeIsEmpty: false,
      };
      this.groups.push(newGroup);
      // Initialize the hint for the new group
      const newIndex = this.groups.length - 1;
      this.$set(this.groupHints, newIndex, { text: '', conflict: false });
      this.updateGroupHint(newIndex);
    },

    /**
     * Remove a filter group.
     * @param {number} index - Index of the group to remove
     */
    removeGroup(index) {
      this.$refs.confirmationPopup.showConfirm(
        'Delete Filter',
        'Are you sure you want to delete this filter?',
        () => {
          this.groups.splice(index, 1);

          // Rebuild groupHints with updated indices
          const newGroupHints = {};
          this.groups.forEach((group, i) => {
            if (this.groupHints[i]) {
              newGroupHints[i] = this.groupHints[i];
            } else {
              newGroupHints[i] = { text: '', conflict: false };
            }
          });
          this.groupHints = newGroupHints;
        },
      );
    },

    /**
     * Update the hint text for a group based on its settings.
     * @param {number} index - Index of the group to update
     */
    updateGroupHint(index) {
      const group = this.groups[index];
      const unitsList = group.units.join(', ');

      // Initialize hint object if it doesn't exist yet
      if (!this.groupHints[index]) {
        this.$set(this.groupHints, index, { text: '', conflict: false });
      }

      // Update the hint text based on the group settings
      if (group.operator === 'listed') {
        if (group.time) {
          if (group.timeIsEmpty) {
            this.$set(this.groupHints[index], 'text', `The incident will be included in the export if any of the listed units (${unitsList}) is present in the report with an empty ${group.time} time.`);
          } else {
            this.$set(this.groupHints[index], 'text', `The incident will be included in the export if any of the listed units (${unitsList}) is present in the report with a non-empty ${group.time} time.`);
          }
        } else {
          this.$set(this.groupHints[index], 'text', `The incident will be included in the export if any of the listed units (${unitsList}) is present in the report.`);
        }
      } else {
        this.$set(this.groupHints[index], 'text', `Incidents containing the units (${unitsList}) will not be included in the export.`);
      }
    },

    handleConflictionFilter() {
      const hasOverlap = (arr1, arr2) => arr1.some(item => arr2.includes(item));
      const unitsPool = {
        listed: [],
        not_listed: [],
      };
      // Initialize the pool with all units
      for (let i = 0; i < this.groups.length; i++) {
        const item = this.groups[i];
        const pool = unitsPool[item.operator];

        unitsPool[item.operator] = pool.concat(item.units);
      }

      for (let i = 0; i < this.groups.length; i++) {
        const item = this.groups[i];
        const units = item.operator === 'listed' ? unitsPool.not_listed : unitsPool.listed;

        // Ensure the hint object exists for this index
        if (!this.groupHints[i]) {
          this.$set(this.groupHints, i, { text: '', conflict: false });
        }

        // Reset conflict status
        this.$set(this.groupHints[i], 'conflict', false);

        if (hasOverlap(item.units, units)) {
          const unitsList = item.units.join(', ');
          this.$set(this.groupHints[i], 'text', `Filter units (${unitsList}) conflict with other filters.`);
          this.$set(this.groupHints[i], 'conflict', true);
        }
      }
    },

    /**
     * Save filters for the current export.
     */
    async saveFilters() {
      if (!this.exportSettings.exportId) {
        return;
      }

      try {
        await profileSettingsApi.updateBillingExportFilters(
          this.exportSettings.exportId,
          this.groups,
        );
      } catch (error) {
        console.error('Error saving filters:', error);
      }
    },

    /**
     * Check for emails list validation and save in success case.
     */
    async validateAndSave() {
      this.showEmailsValidationError = this.exportSettings.notificationEmails.length === 0;
      if (!await this.$validator.validateAll() || this.showEmailsValidationError) {
        if (this.showEmailsValidationError) {
          this.activePanel = 0;
        } else {
          const errorItems = this.$validator.errors ? this.$validator.errors.items : [];
          const panelsMap = {
            newEmail: 0,
            exportType: 1,
            server: 1,
            wsdl: 1,
            organization: 1,
            username: 1,
            newPassword: 1,
            uploadDirectory: 1,
            format: 2,
            cron: 3,
          };
          errorItems.forEach(item => {
            if (Object.prototype.hasOwnProperty.call(panelsMap, item.field)) {
              this.activePanel = panelsMap[item.field];
            }
          });
        }
        return;
      }

      await this.save();

      // Save filters after the main settings are saved
      if (this.exportSettings.exportId) {
        await this.saveFilters();
      }
    },
  },
};
</script>

<style lang="scss" module>
.card {
  padding: 14px;
}

.row {
  display: flex;
}

.column {
  display: flex;
  flex-direction: column;
}

.error {
  color: var(--v-error-base);
  margin-bottom: 10px;
}

.checkboxField {
  margin-top: 0;
}

.exportFormat {
  margin: 10px 0 0 26px;
}

.emailInputContainer {
  display: flex;
  align-items: center;
}

.emailsContainer {
  display: flex;
  flex-wrap: wrap;
}

.emailAddButton {
  margin-left: 10px;
}

.emailChip {
  flex-shrink: 0;
  margin-right: 10px;
  margin-bottom: 10px;
}
.operator-select {
  margin: 0 auto;
}
.group-container {
  background-color: #f5f5f6 !important;
}
.group-toolbar {
  background-color: #515151 !important;
}
.redLabel {
  color: #cc2424;
}
.expansionPanelsContainer {
  max-height: calc(100vh - 350px);
  overflow-y: scroll
}
</style>
